import { Dispatch } from 'react'
import { DropResult } from '@hello-pangea/dnd'
import axios from 'axios'
import * as api from './catalogueApi'
import { CatalogueAction, SidebarOverride } from './catalogueReducer'
import { CatalogueItem, CategoryNode, NoticeKind, ProductColumn, SaveOutcome } from './catalogueTypes'
import { reorderTable } from './catalogueOrdering'
import { t } from '../../i18n'

const SUCCESS_NOTICE_TIMEOUT_MS = 3000

export const createCatalogueActions = (dispatch: Dispatch<CatalogueAction>) => {
  let noticeCounter = 0

  const notify = (kind: NoticeKind, message: string) => {
    noticeCounter += 1
    const id = noticeCounter
    dispatch({ type: 'NOTICE_SET', notice: { id, kind, message } })
    if (kind === 'success') {
      window.setTimeout(() => dispatch({ type: 'NOTICE_CLEARED', id }), SUCCESS_NOTICE_TIMEOUT_MS)
    }
  }

  const reportFailure = (error: unknown) => notify('danger', api.errorMessage(error))

  const refreshTree = () =>
    Promise.all([api.fetchRoots(), api.fetchCategoryOptions()])
      .then(([roots, categoryOptions]) => dispatch({ type: 'TREE_SET', roots, categoryOptions }))
      .catch((error: unknown) => {
        dispatch({ type: 'TREE_FAILED' })
        reportFailure(error)
      })

  const refreshRows = (categoryId: number | null) => {
    if (categoryId === null) return
    api
      .fetchCategoryRows(categoryId)
      .then((rows) => dispatch({ type: 'ROWS_SET', categoryId, rows }))
      .catch(reportFailure)
  }

  const refreshColumns = () =>
    api
      .fetchProductColumns()
      .then((columns) => dispatch({ type: 'COLUMNS_SET', columns }))
      .catch(reportFailure)

  const outcomeOf = (request: Promise<unknown>, onSuccess: () => void): Promise<SaveOutcome> =>
    request
      .then((): SaveOutcome => {
        onSuccess()
        return { ok: true }
      })
      .catch((error: unknown): SaveOutcome => {
        const errors = api.serverErrors(error)
        if (Object.keys(errors).length === 0) reportFailure(error)
        return { ok: false, errors }
      })

  return {
    bootstrap() {
      dispatch({ type: 'TREE_LOADING' })
      refreshTree()
      refreshColumns()
      api
        .fetchProductAttributes()
        .then((productAttributes) => dispatch({ type: 'ATTRIBUTES_SET', productAttributes }))
        .catch(reportFailure)
    },

    loadRows(categoryId: number, signal: AbortSignal) {
      dispatch({ type: 'ROWS_LOADING', categoryId })
      api
        .fetchCategoryRows(categoryId, signal)
        .then((rows) => dispatch({ type: 'ROWS_SET', categoryId, rows }))
        .catch((error: unknown) => {
          if (axios.isCancel(error)) return
          dispatch({ type: 'ROWS_FAILED', categoryId })
          reportFailure(error)
        })
    },

    toggleEditMode: () => dispatch({ type: 'EDIT_MODE_TOGGLED' }),
    setSidebarOverride: (override: SidebarOverride) => dispatch({ type: 'SIDEBAR_OVERRIDE_SET', override }),
    toggleTableCategory: (categoryId: number) => dispatch({ type: 'TABLE_CATEGORY_TOGGLED', categoryId }),

    reorderRoots(roots: CategoryNode[]) {
      dispatch({ type: 'ROOTS_SET', roots })
      api
        .updateRootPositions(roots)
        .then(() => notify('success', t('catalogue.positions_updated')))
        .catch((error: unknown) => {
          reportFailure(error)
          refreshTree()
        })
    },

    reorderTableRows(categoryId: number, rows: CatalogueItem[], result: DropResult) {
      const reordered = reorderTable(rows, result)
      if (!reordered) return
      dispatch({ type: 'ROWS_SET', categoryId, rows: reordered })
      api
        .updateTablePositions(reordered)
        .then(() => notify('success', t('catalogue.positions_updated')))
        .catch((error: unknown) => {
          reportFailure(error)
          refreshRows(categoryId)
        })
    },

    updateColumns(columns: ProductColumn[]) {
      dispatch({ type: 'COLUMNS_MERGED', columns })
      api
        .updateProductColumns(columns)
        .then(() => notify('success', t('catalogue.columns_updated')))
        .catch((error: unknown) => {
          reportFailure(error)
          refreshColumns()
        })
    },

    saveProduct: (form: FormData, productId: number | null, viewedCategoryId: number | null) =>
      outcomeOf(api.saveProduct(form, productId), () => refreshRows(viewedCategoryId)),

    saveCategory: (form: FormData, categoryId: number | null, viewedCategoryId: number | null) =>
      outcomeOf(api.saveCategory(form, categoryId), () => {
        refreshTree()
        refreshRows(viewedCategoryId)
      }),

    requestDelete: (item: CatalogueItem) => dispatch({ type: 'DELETION_REQUESTED', item }),

    confirmDelete(item: CatalogueItem, viewedCategoryId: number | null) {
      api
        .deleteItem(item)
        .then(() => {
          dispatch({ type: 'DELETION_SUCCEEDED' })
          refreshRows(viewedCategoryId)
          if (item.class_name === 'ProductCategory') refreshTree()
        })
        .catch((error: unknown) => dispatch({ type: 'DELETION_FAILED', message: api.deleteErrorMessage(error) }))
    },

    dismissDelete: () => dispatch({ type: 'DELETION_DISMISSED' }),
    dismissNotice: () => dispatch({ type: 'NOTICE_CLEARED', id: noticeCounter })
  }
}

export type CatalogueActions = ReturnType<typeof createCatalogueActions>
